import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Eye, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import InfoTip from "../components/InfoTip";
import { VehicleForm } from "../components/forms";
import PrintButton from "../components/PrintButton";
import PrintHeader from "../components/PrintHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VEH_TYPES, VEHICLE_STATUTS, STATUT_LABELS } from "../lib/constants";

export default function Vehicles() {
  const { hasPerm } = useAuth();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [statut, setStatut] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback((p = page, ps = pageSize, query = q, t = type, st = statut) => {
    setLoading(true);
    API.get("/vehicles", { params: { q: query, type: t, statut: st, page: p, page_size: ps } })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, type, statut]);

  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openCreate = () => { setEditing(null); setForm({ statut: "EN_CIRCULATION", type: "VOITURE" }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row.id);
    API.get(`/vehicles/${row.id}`).then((r) => { setForm(r.data.vehicle); setDialogOpen(true); });
  };

  const save = async () => {
    if (!form.immatriculation) {
      toast.error("L'immatriculation est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, annee: form.annee ? Number(form.annee) : null };
      if (editing) {
        await API.put(`/vehicles/${editing}`, payload);
        toast.success("Vehicule mis a jour.");
      } else {
        await API.post("/vehicles", payload);
        toast.success("Vehicule cree.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
            Vehicules
            <InfoTip testid="tip-vehicle-search" text="Recherchez par immatriculation, VIN, marque ou modele. Les formats GA 123 AA, GA-123-AA et ga123aa sont equivalents." />
          </h1>
          <p className="text-sm text-muted-foreground">Parc de vehicules immatricules et leurs affectations.</p>
        </div>
        {hasPerm("EXPORT_DATA") && <PrintButton testid="print-vehicles" />}
        {hasPerm("CREATE_VEHICLE") && (
          <Button onClick={openCreate} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="vehicle-create">
            <Plus className="h-4 w-4 mr-1" /> Nouveau vehicule
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Immatriculation, VIN, marque, modele..." className="pl-9" data-testid="vehicles-search" />
        </div>
        <Select value={type || "all"} onValueChange={(v) => { setType(v === "all" ? "" : v); load(1, pageSize, q, v === "all" ? "" : v, statut); }}>
          <SelectTrigger className="w-44" data-testid="vehicles-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            {VEH_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statut || "all"} onValueChange={(v) => { setStatut(v === "all" ? "" : v); load(1, pageSize, q, type, v === "all" ? "" : v); }}>
          <SelectTrigger className="w-48" data-testid="vehicles-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {VEHICLE_STATUTS.map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => load(1)} variant="secondary" data-testid="vehicles-apply">Filtrer</Button>
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <>
          {result && <PrintHeader title="Liste des vehicules" subtitle={`${result.total.toLocaleString("fr-FR")} enregistrement(s) — page ${page}`} />}
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Immatriculation", "VIN", "Marque", "Modele", "Type", "Annee", "Conducteur actuel", "Statut", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((v, i) => (
                  <tr key={v.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`vehicle-row-${i}`}>
                    <td className="px-3 py-2.5 mono text-xs font-semibold">{v.immatriculation}</td>
                    <td className="px-3 py-2.5 mono text-xs">{v.vin || "-"}</td>
                    <td className="px-3 py-2.5">{v.marque}</td>
                    <td className="px-3 py-2.5">{v.modele}</td>
                    <td className="px-3 py-2.5 text-xs">{v.type}</td>
                    <td className="px-3 py-2.5 text-xs">{v.annee || "-"}</td>
                    <td className="px-3 py-2.5 text-xs">{v.conducteur || "-"}</td>
                    <td className="px-3 py-2.5"><StatusBadge value={v.statut} /></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Button asChild variant="ghost" size="sm" data-testid={`vehicle-view-${i}`}>
                        <Link to={`/vehicules/${v.id}`}><Eye className="h-4 w-4" /></Link>
                      </Button>
                      {hasPerm("UPDATE_VEHICLE") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(v)} data-testid={`vehicle-edit-${i}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {result && result.items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">Aucun vehicule trouve.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="vehicles-pagination" />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier le vehicule" : "Nouveau vehicule"}</DialogTitle>
          </DialogHeader>
          <VehicleForm initial={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="vehicle-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="vehicle-save">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
